<template>
  <v-card outlined tile>
    <v-toolbar flat>
      <v-spacer />
      <v-btn text color="primary" @click="add">
        <v-icon>add</v-icon>
        Add
      </v-btn>
    </v-toolbar>

    <v-data-table
      dense
      disable-sort
      disable-pagination
      disable-filtering
      hide-default-footer
      item-key="_id"
      class="ma-2"
      :headers="headers"
      :items="registration.brands"
      :loading="$apollo.queries.registration.loading"
    >
      <template v-slot:item="{ item, index }">
        <tr>
          <td>{{ item.name }}</td>
          <td>{{ item.active | yesNo }}</td>
          <td>{{ item.beef | yesNo }}</td>
          <td>{{ item.sheep | yesNo }}</td>
          <td>
            {{ item.packers.map(o => `${_.get(o, 'processor.memberName')} (${o.processorMemberNumber})`).join(', ') }}
          </td>
          <td class="text-end">
            <v-btn small icon @click="edit(item, index)">
              <v-icon color="primary">edit</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" width="500">
      <v-card>
        <v-card-title>Brand</v-card-title>
        <v-card-text>
          <v-form>
            <registration-activity-select
              label="Activity"
              name="activity"
              hint="MSA Activity this Brand is associated with"
              :registrationId="registrationId"
              v-model="form.activityId"
            />
            <v-text-field
              label="Brand Name"
              name="brandName"
              v-model="form.name"
            />
            <v-checkbox
              label="Active"
              name="active"
              v-model="form.active"
            />
            <v-checkbox
              label="Beef"
              name="beef"
              v-model="form.beef"
            />
            <v-checkbox
              label="Sheep"
              name="sheep"
              v-model="form.sheep"
            />

            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>
                      Processor
                    </th>
                    <th>
                      Start
                    </th>
                    <th>
                      End
                    </th>
                    <th class="text-end">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="packer in form.packers"
                    :key="packer._id"
                  >
                    <td>{{ packer.processorMemberNumber }}</td>
                    <td>{{ packer.startDate | formatDate }}</td>
                    <td>{{ packer.endDate | formatDate }}</td>
                    <td class="text-end">
                      <v-btn x-small icon :disabled="!!packer.endDate" @click="packer.endDate = new Date()">
                        <v-icon color="warning">cancel</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <registration-search-field
              name="processorMemberNumber"
              item-value="memberNumber"
              placeholder="Search"
              v-model="newPacker"
              v-bubble.error
            >
              <template slot="append">
                <v-btn outlined style="margin-bottom: 6px" @click="addPacker" :disabled="!newPacker">
                  <v-icon left>add</v-icon>
                  Add
                </v-btn>
              </template>
            </registration-search-field>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="primary"
            text
            :loading="saving"
            @click="save"
          >
            Save
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="dialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import gql from 'graphql-tag'
import { cloneDeep } from 'lodash'

import RegistrationSearchField from './inputs/RegistrationSearchField.vue'
import RegistrationActivitySelect from './inputs/RegistrationActivitySelect.vue'

export default {
  name: 'RegistrationBrands',

  components: {
    RegistrationSearchField,
    RegistrationActivitySelect
  },

  props: {
    registrationId: {
      type: String,
      required: true
    }
  },

  data: () => ({
    registration: {
      brands: []
    },
    headers: [
      { text: 'Brand Name', value: 'name' },
      { text: 'Active', value: 'active' },
      { text: 'Beef', value: 'beef' },
      { text: 'Sheep', value: 'sheep' },
      { text: 'Packers', value: 'packers.length' },
      { text: 'Actions', class: 'text-end' }
    ],
    dialog: false,
    form: {},
    saving: false,
    newPacker: null
  }),

  apollo: {
    registration: {
      query: gql`query GetRegistrationBrands($id: MongoID!) {
        registration: registrationById(_id: $id) {
          _id
          brands {
            _id
            activityId
            name
            active
            beef
            sheep
            packers {
              _id
              startDate
              endDate
              processorMemberNumber
              processor {
                _id
                memberName
              }
            }
          }
        }
      }`,
      variables () {
        return {
          id: this.registrationId
        }
      },
      error (e) { this.$emit('error', e) }
    }
  },

  methods: {
    add () {
      this.form = {
        packers: []
      }
      this.newPacker = null
      this.dialog = true
    },

    edit (brand) {
      this.form = cloneDeep(brand)
      this.newPacker = null
      this.dialog = true
    },

    addPacker () {
      const packer = {
        processorMemberNumber: this.newPacker,
        startDate: new Date()
      }
      this.form.packers.push(packer)
      this.newPacker = null
    },

    async save () {
      this.saving = true

      const { form } = this
      const patch = {
        activityId: form.activityId,
        name: form.name,
        active: form.active,
        beef: form.beef,
        sheep: form.sheep,
        packers: form.packers.map(packer => ({
          _id: packer._id,
          startDate: packer.startDate,
          endDate: packer.endDate,
          processorMemberNumber: packer.processorMemberNumber
        }))
      }

      try {
        if (form._id) {
          await this.$apollo.mutate({
            mutation: gql`mutation ($id: MongoID!, $brandId: MongoID!, $record: UpdateByIdRegistrationBrandsInput!) {
              registrationUpdateBrandById(_id: $id, brandId: $brandId, record: $record) { _id }
            }`,
            variables: {
              id: this.registrationId,
              brandId: form._id,
              record: patch
            }
          })
        } else {
          await this.$apollo.mutate({
            mutation: gql`mutation ($id: MongoID!, $record: RegistrationBrandsInput!) {
              registrationPushBrand(_id: $id, record: $record) { _id }
            }`,
            variables: {
              id: this.registrationId,
              record: patch
            }
          })
        }
      } catch (e) {
        this.$emit('error', e)
        return
      } finally {
        this.saving = false
      }

      this.$apollo.queries.registration.refresh()
      this.dialog = false
      this.form = {}
      this.newPacker = null
      this.$emit('change')
    }
  }
}
</script>
